import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useEffect, useState } from "react";

import FileDropZone from "@/components/common/FileDropZone";
import { uploadDrawingRevision } from "@/features/drawings/api";
import { errorMessage } from "@/lib/api";
import { showSuccess } from "@/lib/toast";

/**
 * 図面 PDF を差し替える（新しい版として登録する）ダイアログ。
 *
 * **版は入力させない**。差し替えのたびに採番の規則を思い出す必要があり、
 * 打ち間違えると同じ版が二重になって登録できなくなる。サーバー側で
 * 「登録済みの数字の版の最大 + 1」を採番する。
 *
 * 前の版は消さずに残る。どの版で見積ったかを後から辿れるようにするためで、
 * 履歴は時計のアイコンから見られる。
 */
export default function DrawingUploadDialog({
  isOpen,
  drawingId,
  drawingNumber,
  isFirst = false,
  onClose,
  onUploaded,
}: {
  isOpen: boolean;
  drawingId: number;
  drawingNumber: string;
  /** まだ 1 枚も登録されていない図面か（文言を「登録」に変える）。 */
  isFirst?: boolean;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 開くたびに初期化する（前回選んだファイルを持ち越さない）。
  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setError(null);
    setBusy(false);
  }, [isOpen]);

  const submit = async () => {
    if (!file) {
      setError("PDF を選んでください。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await uploadDrawingRevision(drawingId, file);
      showSuccess(`版 ${created.revision} として登録しました。`);
      onUploaded();
      onClose();
    } catch (e) {
      setError(errorMessage(e, "PDF を登録できませんでした。"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="lg">
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>{isFirst ? "図面を登録" : "図面を更新"}</span>
          <span className="text-small font-normal text-default-500">
            {isFirst
              ? `${drawingNumber} の図面として登録します。版は自動で採番されます。`
              : `${drawingNumber} の新しい版として登録します。版は自動で採番され、前の版は履歴に残ります。`}
          </span>
        </ModalHeader>

        <ModalBody>
          <FileDropZone
            label="図面PDF"
            accept=".pdf"
            showLabel={false}
            file={file}
            isDisabled={busy}
            onSelect={(selected) => {
              setFile(selected);
              setError(null);
            }}
          />
          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="drawing-upload-submit"
            isDisabled={file == null}
            isLoading={busy}
            onPress={() => void submit()}
          >
            更新
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
